import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const refreshUser = async () => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) return;
    try {
      const response = await authAPI.getMe();
      // Bail out if the token changed while the request was in-flight
      // (e.g. a concurrent login or logout happened)
      if (localStorage.getItem('token') !== storedToken) return;
      const { freshToken, ...userData } = response.data;
      if (freshToken) {
        localStorage.setItem('token', freshToken);
        setToken(freshToken);
      }
      setUser(userData);
    } catch (error) {
      if (localStorage.getItem('token') === storedToken) {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      }
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      await refreshUser();
      setLoading(false);
    };

    initAuth();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshUser();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });
      const { token } = response.data;
      localStorage.setItem('token', token);
      setToken(token);
      await refreshUser();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  const signup = async (name, email, phone_number, password, role = 'user') => {
    try {
      const response = await authAPI.signup({ name, email, phone_number, password, role });
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      setToken(token);
      setUser(user);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Signup failed'
      };
    }
  };

  const googleLogin = async (credential, createIfNotExists = true) => {
    try {
      const response = await authAPI.googleLogin({ credential, createIfNotExists });

      // Check if user needs to accept privacy policy first (new user from login page)
      if (response.data.needsPrivacyAcceptance) {
        return {
          success: false,
          needsPrivacyAcceptance: true,
          email: response.data.email,
          name: response.data.name,
          picture: response.data.picture
        };
      }

      const { token, isNewUser } = response.data;
      localStorage.setItem('token', token);
      setToken(token);
      await refreshUser();
      return { success: true, isNewUser };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Google login failed'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const isSubadmin = () => {
    return user?.role === 'subadmin';
  };

  const isAdminOrSubadmin = () => {
    return user?.role === 'admin' || user?.role === 'subadmin';
  };

  const updateUser = (updatedUserData) => {
    setUser(prevUser => ({ ...prevUser, ...updatedUserData }));
  };

  const value = {
    user,
    token,
    loading,
    login,
    signup,
    googleLogin,
    logout,
    isAdmin,
    isSubadmin,
    isAdminOrSubadmin,
    updateUser,
    isAuthenticated: !!token
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
